import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';
import {
  Accordion,
  AccordionItem,
  Button,
  CopyIcon,
  PlusIcon,
  SaveIcon,
  Select,
  TrashIcon,
  VesselSelect,
} from '../../../components/ui';

type RankOpt = { id: number; code?: string | null; name: string; abrv?: string | null; sortOrder: number };
type VesselTypeOpt = { id: number; code?: string | null; name: string; sortOrder: number };
type VesselOpt = {
  id: number;
  vesselName: string;
  vesselAbrv?: string | null;
  vesselTypeId?: number | null;
};

type MatrixListItem = {
  id: number;
  name: string;
  isActive: boolean;
  vesselTypeId: number | null;
  vesselId: number | null;
  scopeKind: 'type' | 'vessel';
  scopeLabel: string;
  ruleCount: number;
};

type SimilarGroupDraft = {
  key: string;
  id?: number;
  name: string;
  vesselTypeIds: number[];
};

type RuleKind = 'INDIVIDUAL' | 'COMBINED' | 'TOGETHER' | 'STAGGER';
type Metric =
  | 'YEARS_IN_RANK'
  | 'YEARS_IN_RANK_WITH_COMPANY'
  | 'YEARS_WITH_COMPANY'
  | 'YEARS_SAME_VESSEL_TYPE'
  | 'YEARS_SIMILAR_VESSEL_TYPE'
  | 'YEARS_MAIN_ENGINE_TYPE'
  | 'MONTHS_ON_THIS_VESSEL'
  | 'MONTHS_SAILED_TOGETHER';
type Severity = 'WARNING' | 'FAIL';

type RuleDraft = {
  key: string;
  id?: number;
  kind: RuleKind;
  metric: Metric | '';
  rankId: string;
  pairRankId: string;
  similarGroupKey: string;
  minimum: string;
  severity: Severity;
};

type FormResponse = {
  ranks: RankOpt[];
  vesselTypes: VesselTypeOpt[];
  vessels: VesselOpt[];
  similarGroups: { id: number; name: string; sortOrder: number; vesselTypeIds: number[] }[];
  matrices: MatrixListItem[];
  matrix: {
    id: number;
    name: string;
    vesselTypeId: number | null;
    vesselId: number | null;
    isActive: boolean;
    rules: {
      id: number;
      kind: RuleKind;
      metric: Metric | null;
      rankId: number;
      pairRankId: number | null;
      similarGroupId: number | null;
      minimum: number | null;
      severity: Severity;
      sortOrder: number;
    }[];
  } | null;
};

const KIND_OPTIONS = [
  { value: 'INDIVIDUAL', label: 'One person' },
  { value: 'COMBINED', label: 'Combined pair' },
  { value: 'TOGETHER', label: 'Sailed together' },
  { value: 'STAGGER', label: 'Do not change both' },
];

const METRIC_OPTIONS = [
  { value: 'YEARS_IN_RANK', label: 'Years in rank' },
  { value: 'YEARS_IN_RANK_WITH_COMPANY', label: 'Years in rank with company' },
  { value: 'YEARS_WITH_COMPANY', label: 'Years with company' },
  { value: 'YEARS_SAME_VESSEL_TYPE', label: 'Years on this vessel type' },
  { value: 'YEARS_SIMILAR_VESSEL_TYPE', label: 'Years on similar vessel type' },
  { value: 'YEARS_MAIN_ENGINE_TYPE', label: 'Years on this main engine' },
  { value: 'MONTHS_ON_THIS_VESSEL', label: 'Months on this vessel' },
  { value: 'MONTHS_SAILED_TOGETHER', label: 'Months sailed together' },
];

const SEVERITY_OPTIONS = [
  { value: 'WARNING', label: 'Warning' },
  { value: 'FAIL', label: 'Fail' },
];

function newKey() {
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function apiMessage(error: unknown, fallback: string) {
  const msg = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return error instanceof Error && error.message ? error.message : fallback;
}

const emptyRule = (): RuleDraft => ({
  key: newKey(),
  kind: 'INDIVIDUAL',
  metric: 'YEARS_IN_RANK',
  rankId: '',
  pairRankId: '',
  similarGroupKey: '',
  minimum: '1',
  severity: 'WARNING',
});

const FieldLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'mb-1 block text-[10px] font-semibold uppercase tracking-wide',
        colors.textMuted,
        className,
      )}
    >
      {children}
    </span>
  );
};

type Selection =
  | { mode: 'none' }
  | { mode: 'edit'; scopeKind: 'type' | 'vessel'; vesselTypeId: string; vesselId: string }
  | { mode: 'new' };

const CrewMatrixRulesPanel: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const { showError, showSuccess } = useNotification();
  const canEdit = can('crew_parameters', 'edit');

  const [selection, setSelection] = useState<Selection>({ mode: 'none' });
  const [newScopeKind, setNewScopeKind] = useState<'type' | 'vessel'>('type');
  const [newVesselTypeId, setNewVesselTypeId] = useState('');
  const [newVesselId, setNewVesselId] = useState('');

  const [groups, setGroups] = useState<SimilarGroupDraft[]>([]);
  const [rules, setRules] = useState<RuleDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [copyFromTypeId, setCopyFromTypeId] = useState('');
  const [copyFromVesselId, setCopyFromVesselId] = useState('');
  const [showCopy, setShowCopy] = useState(false);

  const scopeKind = selection.mode === 'edit' ? selection.scopeKind : null;

  const formUrl = useMemo(() => {
    if (selection.mode === 'edit' && selection.scopeKind === 'vessel' && selection.vesselId) {
      return `/crew-manning-matrix/form?vesselId=${encodeURIComponent(selection.vesselId)}`;
    }
    if (selection.mode === 'edit' && selection.scopeKind === 'type' && selection.vesselTypeId) {
      return `/crew-manning-matrix/form?vesselTypeId=${encodeURIComponent(selection.vesselTypeId)}`;
    }
    return '/crew-manning-matrix/form';
  }, [selection]);

  const { data, error, isLoading, mutate } = useSWR<FormResponse>(formUrl, fetcher, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (!data) return;
    setGroups(
      (data.similarGroups ?? []).map((g) => ({
        key: `g-${g.id}`,
        id: g.id,
        name: g.name,
        vesselTypeIds: g.vesselTypeIds ?? [],
      })),
    );
    if (selection.mode !== 'edit') {
      setRules([]);
      return;
    }
    const matrix = data.matrix;
    if (!matrix) {
      setRules([]);
      return;
    }
    setRules(
      matrix.rules.map((r) => ({
        key: `r-${r.id}`,
        id: r.id,
        kind: r.kind,
        metric:
          (r.metric as Metric) ||
          (r.kind === 'TOGETHER' ? 'MONTHS_SAILED_TOGETHER' : 'YEARS_IN_RANK'),
        rankId: String(r.rankId),
        pairRankId: r.pairRankId != null ? String(r.pairRankId) : '',
        similarGroupKey: r.similarGroupId != null ? `g-${r.similarGroupId}` : '',
        minimum: r.minimum != null ? String(r.minimum) : '',
        severity: r.severity,
      })),
    );
  }, [data, selection.mode]);

  const matrices = data?.matrices ?? [];

  const rankOptions = useMemo(
    () =>
      (data?.ranks ?? []).map((r) => ({
        value: String(r.id),
        label: r.abrv ? `${r.abrv} — ${r.name}` : r.name,
      })),
    [data?.ranks],
  );

  const vesselTypeOptions = useMemo(
    () => (data?.vesselTypes ?? []).map((t) => ({ value: String(t.id), label: t.name })),
    [data?.vesselTypes],
  );

  const usedTypeIds = useMemo(
    () => new Set(matrices.filter((m) => m.vesselTypeId != null && m.vesselId == null).map((m) => m.vesselTypeId!)),
    [matrices],
  );
  const usedVesselIds = useMemo(
    () => new Set(matrices.filter((m) => m.vesselId != null).map((m) => m.vesselId!)),
    [matrices],
  );

  const availableTypeOptions = useMemo(
    () => vesselTypeOptions.filter((o) => !usedTypeIds.has(Number(o.value))),
    [vesselTypeOptions, usedTypeIds],
  );

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.key, label: g.name || '(unnamed group)' })),
    [groups],
  );

  const selectedListId = useMemo(() => {
    if (selection.mode !== 'edit') return null;
    const hit = matrices.find((m) =>
      selection.scopeKind === 'vessel'
        ? m.vesselId === Number(selection.vesselId)
        : m.vesselTypeId === Number(selection.vesselTypeId) && m.vesselId == null,
    );
    return hit?.id ?? null;
  }, [selection, matrices]);

  const activeLabel = useMemo(() => {
    if (selection.mode !== 'edit') return null;
    if (selection.scopeKind === 'type') {
      return data?.vesselTypes.find((t) => String(t.id) === selection.vesselTypeId)?.name ?? null;
    }
    const v = data?.vessels.find((x) => String(x.id) === selection.vesselId);
    return v?.vesselName ?? null;
  }, [selection, data?.vesselTypes, data?.vessels]);

  const openMatrix = (m: MatrixListItem) => {
    setShowCopy(false);
    setSelection({
      mode: 'edit',
      scopeKind: m.scopeKind,
      vesselTypeId: m.vesselTypeId != null ? String(m.vesselTypeId) : '',
      vesselId: m.vesselId != null ? String(m.vesselId) : '',
    });
  };

  const startNew = () => {
    setShowCopy(false);
    setNewScopeKind('type');
    setNewVesselTypeId('');
    setNewVesselId('');
    setRules([]);
    setSelection({ mode: 'new' });
  };

  const confirmNew = () => {
    if (newScopeKind === 'type' && newVesselTypeId) {
      const existing = matrices.find(
        (m) => m.vesselTypeId === Number(newVesselTypeId) && m.vesselId == null,
      );
      if (existing) {
        openMatrix(existing);
        return;
      }
      setSelection({
        mode: 'edit',
        scopeKind: 'type',
        vesselTypeId: newVesselTypeId,
        vesselId: '',
      });
      setRules([]);
      return;
    }
    if (newScopeKind === 'vessel' && newVesselId) {
      const existing = matrices.find((m) => m.vesselId === Number(newVesselId));
      if (existing) {
        openMatrix(existing);
        return;
      }
      setSelection({
        mode: 'edit',
        scopeKind: 'vessel',
        vesselTypeId: '',
        vesselId: newVesselId,
      });
      setRules([]);
    }
  };

  const addGroup = () => {
    setGroups((prev) => [...prev, { key: newKey(), name: '', vesselTypeIds: [] }]);
  };

  const addRule = () => setRules((prev) => [...prev, emptyRule()]);

  const save = useCallback(async () => {
    if (selection.mode !== 'edit' || !canEdit) return;
    const scopeOk =
      (selection.scopeKind === 'type' && selection.vesselTypeId) ||
      (selection.scopeKind === 'vessel' && selection.vesselId);
    if (!scopeOk) return;

    setSaving(true);
    try {
      const similarGroups = groups.map((g, i) => ({
        id: g.id,
        name: g.name.trim(),
        sortOrder: i,
        vesselTypeIds: g.vesselTypeIds,
      }));
      if (similarGroups.some((g) => !g.name)) {
        showError('Each similar-type group needs a name.');
        setSaving(false);
        return;
      }

      const payloadRules = rules.map((r, i) => {
        const kind = r.kind;
        const metric =
          kind === 'STAGGER'
            ? null
            : kind === 'TOGETHER'
              ? 'MONTHS_SAILED_TOGETHER'
              : (r.metric as Metric);
        const similarGroupId =
          metric === 'YEARS_SIMILAR_VESSEL_TYPE'
            ? groups.find((g) => g.key === r.similarGroupKey)?.id ??
              (r.similarGroupKey.startsWith('g-') ? Number(r.similarGroupKey.slice(2)) : null)
            : null;
        return {
          kind,
          metric,
          rankId: Number(r.rankId),
          pairRankId: kind === 'INDIVIDUAL' ? null : r.pairRankId ? Number(r.pairRankId) : null,
          similarGroupId:
            similarGroupId != null && Number.isFinite(similarGroupId) ? similarGroupId : null,
          minimum: kind === 'STAGGER' ? null : r.minimum === '' ? null : Number(r.minimum),
          severity: r.severity,
          sortOrder: i,
          _groupKey: r.similarGroupKey,
        };
      });

      const groupsWithTemp = similarGroups.map((g, i) => ({
        ...g,
        id: g.id ?? -(i + 1),
      }));
      const keyToTempId = new Map<string, number>();
      groups.forEach((g, i) => {
        keyToTempId.set(g.key, groupsWithTemp[i].id!);
      });

      const rulesFinal = payloadRules.map((r) => {
        const { _groupKey, ...rest } = r;
        if (rest.metric === 'YEARS_SIMILAR_VESSEL_TYPE' && _groupKey) {
          rest.similarGroupId = keyToTempId.get(_groupKey) ?? rest.similarGroupId;
        }
        return rest;
      });

      for (const r of rulesFinal) {
        if (!r.rankId || !Number.isFinite(r.rankId)) {
          showError('Each rule needs a rank.');
          setSaving(false);
          return;
        }
        if (r.kind !== 'INDIVIDUAL' && r.kind !== 'STAGGER' && !r.pairRankId) {
          showError('Pair rules need a second rank.');
          setSaving(false);
          return;
        }
        if (r.kind === 'STAGGER' && !r.pairRankId) {
          showError('Do-not-change-both needs two ranks.');
          setSaving(false);
          return;
        }
        if (r.kind !== 'STAGGER' && (r.minimum == null || Number.isNaN(r.minimum))) {
          showError('Each rule needs a minimum.');
          setSaving(false);
          return;
        }
      }

      await api.put('/crew-manning-matrix/save', {
        vesselTypeId: selection.scopeKind === 'type' ? Number(selection.vesselTypeId) : null,
        vesselId: selection.scopeKind === 'vessel' ? Number(selection.vesselId) : null,
        similarGroups: groupsWithTemp,
        rules: rulesFinal,
      });
      showSuccess('Crew matrix rules saved.');
      await mutate();
    } catch (e) {
      showError(apiMessage(e, 'Could not save matrix rules.'));
    } finally {
      setSaving(false);
    }
  }, [canEdit, groups, mutate, rules, selection, showError, showSuccess]);

  const copyFrom = useCallback(async () => {
    if (selection.mode !== 'edit' || !canEdit) return;
    const fromType = copyFromTypeId ? Number(copyFromTypeId) : null;
    const fromVessel = copyFromVesselId ? Number(copyFromVesselId) : null;
    if (!fromType && !fromVessel) {
      showError('Choose a vessel type or vessel to copy from.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/crew-manning-matrix/copy', {
        fromVesselTypeId: fromVessel ? null : fromType,
        fromVesselId: fromVessel,
        toVesselTypeId: selection.scopeKind === 'type' ? Number(selection.vesselTypeId) : null,
        toVesselId: selection.scopeKind === 'vessel' ? Number(selection.vesselId) : null,
      });
      showSuccess('Rules copied into this matrix.');
      setCopyFromTypeId('');
      setCopyFromVesselId('');
      setShowCopy(false);
      await mutate();
    } catch (e) {
      showError(apiMessage(e, 'Could not copy rules.'));
    } finally {
      setSaving(false);
    }
  }, [
    canEdit,
    copyFromTypeId,
    copyFromVesselId,
    mutate,
    selection,
    showError,
    showSuccess,
  ]);

  const updateRule = (key: string, patch: Partial<RuleDraft>) => {
    setRules((prev) => prev.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  };

  const inputClass = cn(
    'h-8 w-full rounded-md border px-2 text-sm outline-none focus:ring-1 focus:ring-current/20',
    colors.border,
    colors.surface,
    colors.text,
  );

  const typeMatrices = matrices.filter((m) => m.scopeKind === 'type');
  const vesselMatrices = matrices.filter((m) => m.scopeKind === 'vessel');

  return (
    <div className="flex h-full min-h-0">
      {/* Saved matrices list */}
      <aside
        className={cn(
          'flex w-64 shrink-0 flex-col border-r',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className={cn('flex items-center justify-between gap-2 border-b px-3 py-2.5', colors.border)}>
          <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
            Matrices
          </span>
          {canEdit && (
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={startNew}>
              <PlusIcon className="h-3.5 w-3.5" />
              New
            </Button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-2 space-y-3">
          {error && <p className="px-1 text-xs text-red-600">Could not load.</p>}
          {isLoading && !data && (
            <p className={cn('px-1 text-xs', colors.textMuted)}>Loading…</p>
          )}
          {!isLoading && matrices.length === 0 && selection.mode !== 'new' && (
            <p className={cn('px-1 py-4 text-xs', colors.textMuted)}>
              No matrices yet. Create one for a vessel type or a single vessel.
            </p>
          )}

          {typeMatrices.length > 0 && (
            <div>
              <p className={cn('mb-1 px-1 text-[10px] font-semibold uppercase', colors.textMuted)}>
                By vessel type
              </p>
              <ul className="space-y-0.5">
                {typeMatrices.map((m) => {
                  const active = selectedListId === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => openMatrix(m)}
                        className={cn(
                          'flex w-full flex-col rounded-md px-2.5 py-2 text-left transition-colors',
                          active
                            ? isDark
                              ? 'bg-white/10'
                              : 'bg-black/5'
                            : colors.interactiveHover,
                        )}
                      >
                        <span className={cn('text-sm font-medium', colors.text)}>{m.scopeLabel}</span>
                        <span className={cn('text-[11px]', colors.textMuted)}>
                          {m.ruleCount} rule{m.ruleCount === 1 ? '' : 's'}
                          {!m.isActive ? ' · inactive' : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {vesselMatrices.length > 0 && (
            <div>
              <p className={cn('mb-1 px-1 text-[10px] font-semibold uppercase', colors.textMuted)}>
                By vessel (override)
              </p>
              <ul className="space-y-0.5">
                {vesselMatrices.map((m) => {
                  const active = selectedListId === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => openMatrix(m)}
                        className={cn(
                          'flex w-full flex-col rounded-md px-2.5 py-2 text-left transition-colors',
                          active
                            ? isDark
                              ? 'bg-white/10'
                              : 'bg-black/5'
                            : colors.interactiveHover,
                        )}
                      >
                        <span className={cn('text-sm font-medium', colors.text)}>{m.scopeLabel}</span>
                        <span className={cn('text-[11px]', colors.textMuted)}>
                          {m.ruleCount} rule{m.ruleCount === 1 ? '' : 's'}
                          {!m.isActive ? ' · inactive' : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </aside>

      {/* Editor */}
      <div className="flex min-w-0 flex-1 flex-col">
        {selection.mode === 'none' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6">
            <p className={cn('text-sm', colors.textMuted)}>
              Select a matrix on the left, or create a new one.
            </p>
            {canEdit && (
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={startNew}>
                <PlusIcon className="h-3.5 w-3.5" />
                New matrix
              </Button>
            )}
          </div>
        )}

        {selection.mode === 'new' && (
          <div className="flex flex-1 flex-col">
            <div className={cn('border-b px-5 py-3', colors.border, colors.headerBg)}>
              <h3 className={cn('text-sm font-semibold', colors.text)}>New matrix</h3>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                Rules for a vessel type apply to every ship of that type. A vessel matrix overrides
                the type for that ship only.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3 px-5 py-4">
              <div className="w-40">
                <FieldLabel>Apply to</FieldLabel>
                <Select
                  value={newScopeKind}
                  onChange={(e) => {
                    setNewScopeKind(e.target.value as 'type' | 'vessel');
                    setNewVesselTypeId('');
                    setNewVesselId('');
                  }}
                  options={[
                    { value: 'type', label: 'Vessel type' },
                    { value: 'vessel', label: 'One vessel' },
                  ]}
                  size="sm"
                />
              </div>
              {newScopeKind === 'type' ? (
                <div className="w-64">
                  <FieldLabel>Vessel type</FieldLabel>
                  <Select
                    value={newVesselTypeId}
                    onChange={(e) => setNewVesselTypeId(String(e.target.value))}
                    options={availableTypeOptions}
                    placeholder={
                      availableTypeOptions.length ? 'Select…' : 'All types already have a matrix'
                    }
                    size="sm"
                  />
                </div>
              ) : (
                <div className="w-72">
                  <FieldLabel>Vessel</FieldLabel>
                  <VesselSelect
                    name="newMatrixVesselId"
                    value={newVesselId}
                    onChange={(e) => setNewVesselId(String(e.target.value))}
                    placeholder="Select vessel…"
                    includeIds={newVesselId ? [newVesselId] : undefined}
                  />
                  {newVesselId && usedVesselIds.has(Number(newVesselId)) && (
                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                      This vessel already has a matrix — opening it instead is better.
                    </p>
                  )}
                </div>
              )}
              <Button
                type="button"
                size="sm"
                disabled={
                  newScopeKind === 'type' ? !newVesselTypeId : !newVesselId
                }
                onClick={confirmNew}
              >
                Continue
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelection({ mode: 'none' })}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {selection.mode === 'edit' && (
          <>
            <div
              className={cn(
                'flex shrink-0 flex-wrap items-center gap-3 border-b px-5 py-2.5',
                colors.border,
                colors.headerBg,
              )}
            >
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-semibold truncate', colors.text)}>
                  {activeLabel ?? 'Matrix'}
                </p>
                <p className={cn('text-[11px]', colors.textMuted)}>
                  {scopeKind === 'vessel' ? 'Vessel override' : 'Vessel type'} · {rules.length} rule
                  {rules.length === 1 ? '' : 's'}
                </p>
              </div>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={saving}
                    onClick={() => setShowCopy((v) => !v)}
                  >
                    <CopyIcon className="h-3.5 w-3.5" />
                    Copy into this
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="gap-1.5"
                    disabled={saving}
                    onClick={() => void save()}
                  >
                    <SaveIcon className="h-3.5 w-3.5" />
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              )}
            </div>

            {showCopy && canEdit && (
              <div
                className={cn(
                  'flex shrink-0 flex-wrap items-end gap-3 border-b px-5 py-2.5',
                  colors.border,
                  isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]',
                )}
              >
                <div className="w-52">
                  <FieldLabel>From vessel type</FieldLabel>
                  <Select
                    value={copyFromTypeId}
                    onChange={(e) => {
                      setCopyFromTypeId(String(e.target.value));
                      setCopyFromVesselId('');
                    }}
                    options={vesselTypeOptions}
                    placeholder="Vessel type…"
                    size="sm"
                    showClearButton={Boolean(copyFromTypeId)}
                  />
                </div>
                <div className="w-60">
                  <FieldLabel>Or from vessel</FieldLabel>
                  <VesselSelect
                    name="copyFromVesselId"
                    value={copyFromVesselId}
                    onChange={(e) => {
                      setCopyFromVesselId(String(e.target.value));
                      setCopyFromTypeId('');
                    }}
                    placeholder="Vessel…"
                    includeIds={copyFromVesselId ? [copyFromVesselId] : undefined}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  disabled={saving || (!copyFromTypeId && !copyFromVesselId)}
                  onClick={() => void copyFrom()}
                >
                  Apply copy
                </Button>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-auto">
              <div className="space-y-6 px-5 py-4">
                <Accordion className="space-y-0">
                  <AccordionItem
                    title="How to fill rules"
                    description="Rule kinds, measures, and examples"
                    defaultOpen={false}
                    headerClassName="px-3 py-2"
                    className={cn('rounded-lg border', colors.border, colors.surface)}
                  >
                    <div className={cn('space-y-3 text-xs leading-relaxed', colors.textSecondary)}>
                      <p>
                        Left list shows every saved matrix. Vessel-type rules apply to all ships of
                        that type; a vessel matrix overrides the type for that ship only.
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>
                          One person — one rank meets a minimum (e.g. C/O, years in rank ≥ 1).
                        </li>
                        <li>
                          Combined pair — sum of two ranks (e.g. MST + C/O years in rank ≥ 5).
                        </li>
                        <li>
                          Sailed together — months the pair served on the same ship.
                        </li>
                        <li>
                          Do not change both — fails if both ranks have an open relief. No minimum.
                        </li>
                      </ul>
                      <p>
                        Severity Warning or Fail is display-only on the officer matrix and relief
                        pool; assigning is never blocked.
                      </p>
                    </div>
                  </AccordionItem>
                </Accordion>

                <section>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3
                      className={cn(
                        'text-xs font-semibold uppercase tracking-wide',
                        colors.textMuted,
                      )}
                    >
                      Similar vessel types
                    </h3>
                    {canEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1 h-7"
                        onClick={addGroup}
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Group
                      </Button>
                    )}
                  </div>
                  {groups.length === 0 ? (
                    <p className={cn('text-xs', colors.textMuted)}>
                      Optional — for “years on similar vessel type” rules.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {groups.map((g) => (
                        <div
                          key={g.key}
                          className={cn(
                            'flex flex-wrap items-start gap-3 rounded-lg border px-3 py-2.5',
                            colors.border,
                            colors.surface,
                          )}
                        >
                          <div className="w-40 shrink-0">
                            <input
                              className={inputClass}
                              value={g.name}
                              disabled={!canEdit}
                              placeholder="Group name"
                              onChange={(e) =>
                                setGroups((prev) =>
                                  prev.map((x) =>
                                    x.key === g.key ? { ...x, name: e.target.value } : x,
                                  ),
                                )
                              }
                            />
                          </div>
                          <div className="min-w-[12rem] flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {g.vesselTypeIds.map((id) => {
                                const label =
                                  data?.vesselTypes.find((t) => t.id === id)?.name ?? String(id);
                                return (
                                  <span
                                    key={id}
                                    className={cn(
                                      'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs',
                                      colors.border,
                                      colors.text,
                                    )}
                                  >
                                    {label}
                                    {canEdit && (
                                      <button
                                        type="button"
                                        className={cn('leading-none', colors.textMuted)}
                                        aria-label={`Remove ${label}`}
                                        onClick={() =>
                                          setGroups((prev) =>
                                            prev.map((x) =>
                                              x.key === g.key
                                                ? {
                                                    ...x,
                                                    vesselTypeIds: x.vesselTypeIds.filter(
                                                      (t) => t !== id,
                                                    ),
                                                  }
                                                : x,
                                            ),
                                          )
                                        }
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                              {canEdit && (
                                <div className="w-40">
                                  <Select
                                    value=""
                                    onChange={(e) => {
                                      const id = Number(e.target.value);
                                      if (!Number.isFinite(id)) return;
                                      setGroups((prev) =>
                                        prev.map((x) =>
                                          x.key === g.key && !x.vesselTypeIds.includes(id)
                                            ? { ...x, vesselTypeIds: [...x.vesselTypeIds, id] }
                                            : x,
                                        ),
                                      );
                                    }}
                                    options={vesselTypeOptions.filter(
                                      (o) => !g.vesselTypeIds.includes(Number(o.value)),
                                    )}
                                    placeholder="Add type…"
                                    size="sm"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              title="Remove group"
                              className={cn(
                                'mt-1 rounded-md p-1.5 transition-colors',
                                colors.textMuted,
                                colors.interactiveHover,
                              )}
                              onClick={() =>
                                setGroups((prev) => prev.filter((x) => x.key !== g.key))
                              }
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3
                      className={cn(
                        'text-xs font-semibold uppercase tracking-wide',
                        colors.textMuted,
                      )}
                    >
                      Rules
                    </h3>
                    {canEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1 h-7"
                        onClick={addRule}
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Rule
                      </Button>
                    )}
                  </div>

                  {isLoading && !data?.matrix && rules.length === 0 ? (
                    <p className={cn('text-sm', colors.textMuted)}>Loading rules…</p>
                  ) : rules.length === 0 ? (
                    <div
                      className={cn(
                        'rounded-lg border border-dashed px-4 py-10 text-center',
                        colors.border,
                      )}
                    >
                      <p className={cn('text-sm', colors.textMuted)}>No rules yet.</p>
                      {canEdit && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-3 gap-1.5"
                          onClick={addRule}
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          Add first rule
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className={cn('overflow-hidden rounded-lg border', colors.border)}>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] border-collapse text-sm">
                          <thead>
                            <tr className={cn(colors.headerBg)}>
                              {(
                                [
                                  'Kind',
                                  'Rank',
                                  'Pair',
                                  'Measure',
                                  'Similar group',
                                  'Min',
                                  'Severity',
                                  '',
                                ] as const
                              ).map((h) => (
                                <th
                                  key={h || 'actions'}
                                  className={cn(
                                    'border-b px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide',
                                    colors.border,
                                    colors.textMuted,
                                  )}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rules.map((r, idx) => {
                              const needsPair = r.kind !== 'INDIVIDUAL';
                              const needsMetric = r.kind !== 'STAGGER';
                              const metricLocked = r.kind === 'TOGETHER';
                              const needsSimilar = r.metric === 'YEARS_SIMILAR_VESSEL_TYPE';
                              return (
                                <tr
                                  key={r.key}
                                  className={cn(
                                    'align-middle',
                                    idx % 2 === 1 &&
                                      (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.015]'),
                                  )}
                                >
                                  <td className={cn('border-b px-2 py-1.5', colors.border)}>
                                    <Select
                                      value={r.kind}
                                      disabled={!canEdit}
                                      size="sm"
                                      options={KIND_OPTIONS}
                                      onChange={(e) => {
                                        const kind = e.target.value as RuleKind;
                                        updateRule(r.key, {
                                          kind,
                                          metric:
                                            kind === 'TOGETHER'
                                              ? 'MONTHS_SAILED_TOGETHER'
                                              : kind === 'STAGGER'
                                                ? ''
                                                : r.metric || 'YEARS_IN_RANK',
                                        });
                                      }}
                                    />
                                  </td>
                                  <td className={cn('border-b px-2 py-1.5', colors.border)}>
                                    <Select
                                      value={r.rankId}
                                      disabled={!canEdit}
                                      size="sm"
                                      options={rankOptions}
                                      placeholder="Rank…"
                                      onChange={(e) =>
                                        updateRule(r.key, { rankId: String(e.target.value) })
                                      }
                                    />
                                  </td>
                                  <td className={cn('border-b px-2 py-1.5', colors.border)}>
                                    <Select
                                      value={r.pairRankId}
                                      disabled={!canEdit || !needsPair}
                                      size="sm"
                                      options={rankOptions}
                                      placeholder={needsPair ? 'Second…' : '—'}
                                      showClearButton={Boolean(r.pairRankId)}
                                      onChange={(e) =>
                                        updateRule(r.key, {
                                          pairRankId: String(e.target.value),
                                        })
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      'border-b px-2 py-1.5 min-w-[11rem]',
                                      colors.border,
                                    )}
                                  >
                                    <Select
                                      value={metricLocked ? 'MONTHS_SAILED_TOGETHER' : r.metric}
                                      disabled={!canEdit || !needsMetric || metricLocked}
                                      size="sm"
                                      options={METRIC_OPTIONS.filter((m) =>
                                        r.kind === 'TOGETHER'
                                          ? m.value === 'MONTHS_SAILED_TOGETHER'
                                          : true,
                                      )}
                                      placeholder="—"
                                      onChange={(e) =>
                                        updateRule(r.key, { metric: e.target.value as Metric })
                                      }
                                    />
                                  </td>
                                  <td className={cn('border-b px-2 py-1.5', colors.border)}>
                                    <Select
                                      value={r.similarGroupKey}
                                      disabled={!canEdit || !needsSimilar}
                                      size="sm"
                                      options={groupOptions}
                                      placeholder={needsSimilar ? 'Group…' : '—'}
                                      showClearButton={Boolean(r.similarGroupKey)}
                                      onChange={(e) =>
                                        updateRule(r.key, {
                                          similarGroupKey: String(e.target.value),
                                        })
                                      }
                                    />
                                  </td>
                                  <td className={cn('border-b px-2 py-1.5 w-20', colors.border)}>
                                    <input
                                      className={cn(inputClass, 'text-center tabular-nums')}
                                      value={r.kind === 'STAGGER' ? '' : r.minimum}
                                      disabled={!canEdit || r.kind === 'STAGGER'}
                                      inputMode="decimal"
                                      placeholder={r.kind === 'STAGGER' ? '—' : '0'}
                                      onChange={(e) =>
                                        updateRule(r.key, { minimum: e.target.value })
                                      }
                                    />
                                  </td>
                                  <td className={cn('border-b px-2 py-1.5 w-28', colors.border)}>
                                    <Select
                                      value={r.severity}
                                      disabled={!canEdit}
                                      size="sm"
                                      options={SEVERITY_OPTIONS}
                                      onChange={(e) =>
                                        updateRule(r.key, {
                                          severity: e.target.value as Severity,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className={cn('border-b px-1 py-1.5 w-10', colors.border)}>
                                    {canEdit && (
                                      <button
                                        type="button"
                                        title="Remove rule"
                                        className={cn(
                                          'rounded-md p-1.5 transition-colors',
                                          colors.textMuted,
                                          colors.interactiveHover,
                                        )}
                                        onClick={() =>
                                          setRules((prev) =>
                                            prev.filter((x) => x.key !== r.key),
                                          )
                                        }
                                      >
                                        <TrashIcon className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CrewMatrixRulesPanel;
